// rateBandValidationUtil.js
//
// Checks a sport's rate-band schedule and turns problems into messages an
// admin can act on, rather than leaving them to discover a mispriced booking
// later.
//
// Bands are day-scoped and can wrap past midnight (see the rate-band section
// of bookingUtils.js), which makes overlaps and gaps genuinely hard to spot
// by eye: "Peak 5PM-6AM on weekends" and "Late 10PM-2AM every day" collide on
// Saturday and Sunday only, and nothing about reading the two rows says so.
//
// The checks run per (day, hour) cell — 7 x 24 — which is small enough to be
// exhaustive and means every result is a fact rather than a heuristic.

import { WEEKDAYS, isHourInBand, bandAppliesOnDay, formatHourLabel, parseTimeSlotStartHour } from './bookingUtils';
import { formatMoney } from './currencyUtil';

/** Severity levels, in the order they should be shown. */
export const ISSUE_LEVELS = { ERROR: 'error', WARNING: 'warning' };

/**
 * Collapses a sorted list of day indexes into a short label:
 * [1,2,3,4,5] -> "Weekdays", [0,6] -> "Weekends", [1,3] -> "Mon, Wed".
 * @param {number[]} days
 * @returns {string}
 */
const describeDays = (days) => {
  const sorted = [...new Set(days.map(Number))].sort((a, b) => a - b);
  if (sorted.length === 7) return 'every day';
  if (sorted.length === 5 && [1, 2, 3, 4, 5].every(d => sorted.includes(d))) return 'weekdays';
  if (sorted.length === 2 && sorted.includes(0) && sorted.includes(6)) return 'weekends';
  return sorted.map(d => WEEKDAYS[d]?.short).filter(Boolean).join(', ');
};

/**
 * Collapses consecutive hours into ranges so a message reads
 * "6AM-9AM, 10PM" instead of listing six separate hours.
 * @param {number[]} hours
 * @returns {string}
 */
const describeHours = (hours) => {
  const sorted = [...new Set(hours.map(Number))].sort((a, b) => a - b);
  if (sorted.length === 0) return '';
  const parts = [];
  let runStart = sorted[0];
  let prev = sorted[0];
  const flush = () => {
    parts.push(runStart === prev
      ? formatHourLabel(runStart)
      : `${formatHourLabel(runStart)}–${formatHourLabel(prev + 1)}`);
  };
  sorted.slice(1).forEach((h) => {
    if (h === prev + 1) { prev = h; return; }
    flush();
    runStart = h;
    prev = h;
  });
  flush();
  return parts.join(', ');
};

/** A band's display name, falling back to its hours when unnamed. */
const bandName = (band) => (band?.name || '').trim()
  || `${formatHourLabel(Number(band?.startHour) || 0)}–${formatHourLabel(Number(band?.endHour) || 0)}`;

/**
 * The distinct start hours a club actually sells, derived from its configured
 * time slots. Gaps are only reported against these — an hour the club never
 * opens doesn't need a rate.
 * @param {string[]} timeSlots
 * @returns {number[]}
 */
const openHoursFromSlots = (timeSlots) => [...new Set(
  (timeSlots || []).map(slot => parseTimeSlotStartHour(slot)).filter(h => Number.isFinite(h))
)];

/**
 * Validates one sport's rate bands.
 *
 * Returns a flat, ready-to-render list. Each issue carries a `level`, a
 * `message` written for an admin (no jargon, names the bands and the exact
 * days/hours involved), and the `bandIds` involved so a caller can highlight
 * the offending rows.
 *
 * Checks, in order of severity:
 *   1. ERROR   — two bands cover the same hour on the same day. Whichever is
 *                listed first silently wins, so the other row is dead.
 *   2. ERROR   — a band with no price, or a negative one.
 *   3. WARNING — open hours no band covers on some day; getCourtRate falls
 *                back to the cheapest band there.
 *   4. WARNING — a band that can never apply (no days ticked is treated as
 *                every day, but an explicitly impossible hour range isn't).
 *
 * @param {Array<object>} bands
 * @param {{timeSlots?: string[]}} [options]
 * @returns {Array<{level: string, code: string, message: string, bandIds: string[]}>}
 */
export const validateRateBands = (bands, options = {}) => {
  // Pulled off the options bag rather than added as a positional argument:
  // callers that never pass it (packageValidationUtil) keep working, and an
  // amount in a warning then falls back to the default symbol.
  const { config = null } = options;
  const list = Array.isArray(bands) ? bands.filter(Boolean) : [];
  const issues = [];

  if (list.length === 0) {
    return [{
      level: ISSUE_LEVELS.WARNING,
      code: 'no-bands',
      message: 'No hourly rates set for this sport yet — add at least one so bookings can be priced.',
      bandIds: [],
    }];
  }

  // --- 2. missing or nonsensical prices -------------------------------
  list.forEach((band) => {
    const price = Number(band.price);
    if (!Number.isFinite(price)) {
      issues.push({
        level: ISSUE_LEVELS.ERROR,
        code: 'price-missing',
        message: `"${bandName(band)}" has no price — bookings in those hours would be charged ${formatMoney(0, config)}.`,
        bandIds: [band.id],
      });
    } else if (price < 0) {
      issues.push({
        level: ISSUE_LEVELS.ERROR,
        code: 'price-negative',
        message: `"${bandName(band)}" has a negative price (${formatMoney(price, config)}).`,
        bandIds: [band.id],
      });
    }
  });

  // --- 1. overlaps, computed per (day, hour) --------------------------
  // Keyed by the PAIR of bands so one message covers every day and hour they
  // collide on, instead of one message per cell.
  const overlaps = new Map();
  for (let day = 0; day < 7; day += 1) {
    for (let hour = 0; hour < 24; hour += 1) {
      const covering = list.filter(b => bandAppliesOnDay(b, day) && isHourInBand(b, hour));
      for (let i = 1; i < covering.length; i += 1) {
        // Only the first band ever wins, so every later one is what's dead —
        // pair each against the winner rather than against each other.
        const key = `${covering[0].id}|${covering[i].id}`;
        if (!overlaps.has(key)) {
          overlaps.set(key, { winner: covering[0], loser: covering[i], days: new Set(), hours: new Set() });
        }
        overlaps.get(key).days.add(day);
        overlaps.get(key).hours.add(hour);
      }
    }
  }
  overlaps.forEach(({ winner, loser, days, hours }) => {
    issues.push({
      level: ISSUE_LEVELS.ERROR,
      code: 'overlap',
      message: `"${bandName(winner)}" and "${bandName(loser)}" both cover ${describeHours([...hours])} on ${describeDays([...days])}. `
        + `"${bandName(winner)}" wins because it's listed first, so those hours will never charge the "${bandName(loser)}" rate.`,
      bandIds: [winner.id, loser.id],
    });
  });

  // --- 3. gaps against the hours the club actually opens ---------------
  const openHours = openHoursFromSlots(options.timeSlots);
  if (openHours.length > 0) {
    // Keyed by the set of uncovered hours so days sharing a gap collapse into
    // one message ("Sat, Sun") rather than repeating it per day.
    const gaps = new Map();
    for (let day = 0; day < 7; day += 1) {
      const uncovered = openHours.filter(
        hour => !list.some(b => bandAppliesOnDay(b, day) && isHourInBand(b, hour))
      );
      if (uncovered.length === 0) continue;
      const key = uncovered.sort((a, b) => a - b).join(',');
      if (!gaps.has(key)) gaps.set(key, { hours: uncovered, days: [] });
      gaps.get(key).days.push(day);
    }
    gaps.forEach(({ hours, days }) => {
      issues.push({
        level: ISSUE_LEVELS.WARNING,
        code: 'gap',
        message: `No rate covers ${describeHours(hours)} on ${describeDays(days)} — `
          + 'those hours will be charged at your cheapest rate until a band covers them.',
        bandIds: [],
      });
    });
  }

  // --- 4. bands that can never apply ----------------------------------
  list.forEach((band) => {
    const days = Array.isArray(band.days) ? band.days : [];
    // A band explicitly scoped to no days at all can't be expressed through
    // the day picker (unticking everything means "every day"), but a config
    // edited by hand could contain one.
    if (Array.isArray(band.days) && band.days.length === 0 && band.daysExplicit === true) {
      issues.push({
        level: ISSUE_LEVELS.WARNING,
        code: 'no-days',
        message: `"${bandName(band)}" applies to no days, so it will never be used.`,
        bandIds: [band.id],
      });
    }
    if (days.length > 0 && openHours.length > 0) {
      const everUsed = days.some(day => openHours.some(
        hour => bandAppliesOnDay(band, day) && isHourInBand(band, hour)
      ));
      if (!everUsed) {
        issues.push({
          level: ISSUE_LEVELS.WARNING,
          code: 'never-applies',
          message: `"${bandName(band)}" doesn't overlap any of your open hours on ${describeDays(days)}, so it will never be used.`,
          bandIds: [band.id],
        });
      }
    }
  });

  // Errors first — an admin should fix a silently-dead band before chasing a
  // gap warning.
  return issues.sort((a, b) => (a.level === b.level ? 0 : a.level === ISSUE_LEVELS.ERROR ? -1 : 1));
};

/**
 * Whether a schedule has anything that would actually mis-charge a booking.
 * Warnings alone don't block saving — an admin mid-edit will often have a
 * temporary gap.
 * @param {Array<object>} bands
 * @param {{timeSlots?: string[]}} [options]
 * @returns {boolean}
 */
export const hasRateBandErrors = (bands, options = {}) =>
  validateRateBands(bands, options).some(i => i.level === ISSUE_LEVELS.ERROR);
